import React, { useState, useEffect, useContext } from "react";
import { Card, DatePicker, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { SocketIOContext } from "../../provider/socketprovider";
import ReactApexChart from "react-apexcharts";
import {
  useFetchSensorListQuery,
  useFetchSensorSeriesQuery,
} from "./home.service";
import {
  useFetchCloseChecklistStatusQuery,
  useFetchOpenChecklistStatusQuery,
  useFetchHotholdChecklistStatusQuery,
  useFetchWeeklyChecklistStatusQuery,
  useFetchMonthlyChecklistStatusQuery,
} from "../opencheck/checklists.service";
import { useFetchChecklistSettingsQuery } from "../settings/settings.service";
import { useFetchSensorLatestTempByRestaurantQuery } from "../temperatures/temperature.service";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import "./style.css";

const Dashboard = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [sensorTemps, setSensorTemps] = useState({});

  const tempType = useSelector((store) => store.temperature.temperatureType);

  const { sensorTemperatures, connectSocket, socket, disconnectSocket } =
    useContext(SocketIOContext);

  const [rangeDate, setRangeDate] = useState([
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ]);

  useEffect(() => {
    if (!socket) connectSocket();
    return () => {
      disconnectSocket();
    };
  }, []);

  useEffect(() => {
    setSensorTemps((e) => ({ ...e, ...sensorTemperatures }));
  }, [sensorTemperatures]);

  const { data: checklistSettingsResponse } = useFetchChecklistSettingsQuery();
  const checklistSettings = checklistSettingsResponse?.data || checklistSettingsResponse || {};

  const { data: openChecklistStatusData = {} } =
    useFetchOpenChecklistStatusQuery();
  const { data: closeChecklistStatusData = {} } =
    useFetchCloseChecklistStatusQuery();
  const { data: hotholdChecklistStatusData = {} } =
    useFetchHotholdChecklistStatusQuery();
  const { data: weeklyChecklistStatusData = {} } =
    useFetchWeeklyChecklistStatusQuery();
  const { data: monthlyChecklistStatusData = {} } =
    useFetchMonthlyChecklistStatusQuery();

  const { data: latestTemp = [], isSuccess: isFetchLatestTempSuccess } =
    useFetchSensorLatestTempByRestaurantQuery();

  const { data: sensorlist } = useFetchSensorListQuery();
  const {
    data: sensorchartData,
    isLoading,
    isSuccess,
  } = useFetchSensorSeriesQuery({
    startDate: dayjs(rangeDate[0]).format("YYYY-MM-DD"),
    endDate: dayjs(rangeDate[1]).format("YYYY-MM-DD"),
  });
  // Initialize empty objects to structure the data for chart
  const [sensorChart, setSensorChart] = useState({
    labels: [], // Dates for the x-axis
    datasets: [],
  });
  useEffect(() => {
    if (isSuccess) {
      const chartData = {
        labels: [], // Dates for the x-axis
        datasets: [], // Series for each sensor
      };
      const temperatureDataByDevice = {};

      // Populate the data by looping through the result
      sensorchartData.forEach((item) => {
        const date = item._id.date;
        const deviceId = item.device_id;
        const avgTemp = item.averageTemperature;

        // Ensure the date is included in labels
        if (!chartData.labels.includes(date)) {
          chartData.labels.push(date);
        }

        // If the device doesn't have an entry in datasets, create one
        if (!temperatureDataByDevice[deviceId]) {
          temperatureDataByDevice[deviceId] = {
            label: deviceId, // Device label (for the legend)
            name: deviceId, // Device label (for the legend)
            data: new Array(chartData?.labels?.length).fill(null), // Initialize an array with the same number of labels
            borderColor:
              deviceId === "sensor-1"
                ? "rgb(255, 99, 132)"
                : "rgb(54, 162, 235)", // Different color for each device
            backgroundColor:
              deviceId === "sensor-1"
                ? "rgba(255, 99, 132, 0.2)"
                : "rgba(54, 162, 235, 0.2)", // Color for fill
            fill: false,
          };
        }

        // Find the index of the date and update the device's data at that index
        const dateIndex = chartData.labels.indexOf(date);
        temperatureDataByDevice[deviceId].data[dateIndex] = avgTemp.toFixed(2);
      });

      // Push each device's data into the datasets array
      Object.values(temperatureDataByDevice).forEach((deviceData) => {
        chartData.datasets.push(deviceData);
      });
      setSensorChart(chartData);
    }
  }, [sensorchartData]);

  useEffect(() => {
    if (isFetchLatestTempSuccess)
      setSensorTemps(
        latestTemp.reduce((obj, cur) => {
          obj[cur.dev_eui] = cur.temperature;
          return obj;
        }, {})
      );
  }, [isFetchLatestTempSuccess]);

  // Create a map to store temperature data by device_id

  // Now `chartData` is structured properly for use in a chart library
  const options = {
    chart: {
      type: "line", // Line chart
      height: "400",
      zoom: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      categories: sensorChart?.labels, // Dates for x-axis
      title: {
        text: "Date",
      },
    },
    yaxis: {
      title: {
        text: "Average Temperature (°C)",
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
    legend: {
      position: "top",
    },
  };

  return (
    <Card
      title={t("HOME")}
      style={{ fontSize: "20px" }}
      className="home-page"
      bordered={false}
    >
      <div className="home-page-check-list">
        <div className= "home-page-check-list-label-default">{t("OPENING CHECKS")}</div>
        <div
          className={`home-page-check-list-status ${
            openChecklistStatusData.completed
              ? "home-page-check-list-status-complete"
              : "home-page-check-list-status-pending"
          }`}
          onClick={() => {
            if (!openChecklistStatusData.completed)
              navigate("/openchecks/create");
          }}
        >
          <div>
            {openChecklistStatusData.completed
              ? t("COMPLETED")
              : t("CLICK TO FILL")}
          </div>
        </div>
      </div>
      <div className="home-page-check-list">
        <div className="home-page-check-list-label-default">{t("PROBE CHECKS")}</div>
        <div
          className={`home-page-check-list-status ${
            hotholdChecklistStatusData.completed
              ? "home-page-check-list-status-complete"
              : "home-page-check-list-status-pending"
          }`}
          onClick={() => {
            if (!hotholdChecklistStatusData.completed)
              navigate("/provechecks/create");
          }}
        >
          <div>
            {hotholdChecklistStatusData.completed
              ? t("COMPLETED")
              : t("CLICK TO FILL")}
          </div>
        </div>
      </div>
      <div className="home-page-check-list">
         <div className= "home-page-check-list-label-default">
          <div>{t("CLOSING CHECKS")}</div>
        </div>
        <div
          className={`home-page-check-list-status ${
            closeChecklistStatusData.completed
              ? "home-page-check-list-status-complete"
              : "home-page-check-list-status-pending"
          }`}
          onClick={() => {
            if (!closeChecklistStatusData.completed)
              navigate("/closechecks/create");
          }}
        >
          <div>
            {closeChecklistStatusData.completed
              ? t("COMPLETED")
              : t("CLICK TO FILL")}
          </div>
        </div>
      </div>
      <div className="home-page-check-list">
        <div className="home-page-check-list-label-default">
          <div>{t("WEEKLY CHECKS")}</div>
        </div>
        <div
          className={`home-page-check-list-status ${
            weeklyChecklistStatusData.completed
              ? "home-page-check-list-status-complete"
              : "home-page-check-list-status-pending"
          }`}
          onClick={() => {
            if (!weeklyChecklistStatusData.completed)
              navigate("/weeklychecks/create");
          }}
        >
          <div>
            {weeklyChecklistStatusData.completed
              ? t("COMPLETED")
              : t("CLICK TO FILL")}
          </div>
        </div>
      </div>
            {/* ✅ 5. MONTHLY CHECKS - CONDITIONALLY RENDERED */}
      {checklistSettings?.monthly === true && (
        <div className="home-page-check-list">
          <div className="home-page-check-list-label-default">
            <div>{t("MONTHLY CHECKS")}</div>
          </div>
          <div
            className={`home-page-check-list-status ${
              monthlyChecklistStatusData.completed
                ? "home-page-check-list-status-complete"
                : "home-page-check-list-status-pending"
            }`}
            onClick={() => {
              if (!monthlyChecklistStatusData.completed)
                navigate("/monthlychecks/create");
            }}
          >
            <div>
              {monthlyChecklistStatusData.completed
                ? t("COMPLETED")
                : t("CLICK TO FILL")}
            </div>
          </div>
        </div>
      )}
      <div className="home-page-check-list">
        <div className="home-page-check-list-label-default">
            {t("DELIVERIES")} - {t("ADD")}
        </div>
        <div
          className="home-page-check-list-status home-page-add-default"
          onClick={() => {
              navigate("/delivery");
          }}
        >
              {t("ADD DELIVERIES")}
        </div>
      </div>
      <div className="home-page-check-list">
        <div className="home-page-check-list-label-default">
            {t("INCIDENTS")} - {t("ADD")}
        </div>
        <div
          className="home-page-check-list-status  home-page-add-default"
          onClick={() => navigate("/incidents/create")}
        >
          {t("CREATE INCIDENT")}
        </div>
      </div>

      <div className="home-page-sensor">
        {sensorlist?.data.map((item, key) => (
          <div
            className={`home-page-sensor-item ${
              sensorTemps[item.dev_eui] >= item.min_temp &&
              sensorTemps[item.dev_eui] <= item.max_temp
                ? "home-page-sensor-item-success"
                : "home-page-sensor-item-danger"
            }`}
            key={key}
          >
            <div>{item?.sensor_id}</div>
            <div>
              {sensorTemps[item.dev_eui] !== undefined
                ? tempType === "celsius"
                  ? `${sensorTemps[item.dev_eui]}°C`
                  : `${((sensorTemps[item.dev_eui] * 9) / 5 + 32).toFixed(2)}°F`
                : "Loading..."}
            </div>
          </div>
        ))}
      </div>

      <div className="home-page-chart">
        <div className="home-page-chart-label">
    
        </div>
        <div className="home-page-chart-value">
          <DatePicker.RangePicker
            value={rangeDate}
            allowClear={false}
            onChange={(date, dateString) => setRangeDate(date)}
          />
        </div>
      </div>

      {!isLoading && (
        <Card>
          <ReactApexChart
            options={options}
            series={sensorChart?.datasets}
            type="bar"
            height={400}
          />
        </Card>
      )}
    </Card>
  );
};

export default Dashboard;
